import { useState } from "react";
import {
  Stack, Group, Text, ActionIcon, Anchor, Collapse, Badge, Tooltip, Paper,
} from "@mantine/core";
import {
  IconPlayerPlayFilled, IconX, IconChevronDown, IconChevronUp,
} from "@tabler/icons-react";
import { useRecentStore } from "../../store/recentStore";
import { useAppStore } from "../../store/appStore";
import type { RecentValidation } from "../../types";
import classes from "./RecentValidations.module.css";

function timeAgo(ts: number): string {
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatCoord(lon: number, lat: number): string {
  return (
    `${Math.abs(lat).toFixed(2)}°${lat >= 0 ? "N" : "S"}, ` +
    `${Math.abs(lon).toFixed(2)}°${lon >= 0 ? "E" : "W"}`
  );
}

interface EntryCardProps {
  entry: RecentValidation;
  onLoad: (entry: RecentValidation) => void;
  onRun: (entry: RecentValidation) => void;
  onRemove: (id: string) => void;
}

function EntryCard({ entry, onLoad, onRun, onRemove }: EntryCardProps) {
  const unit = entry.layer === "wave" ? "m" : "m/s";
  const biasStr = (entry.stats.bias >= 0 ? "+" : "") + entry.stats.bias.toFixed(2);

  return (
    <Paper
      withBorder
      p={6}
      radius="sm"
      className={classes.card}
      onClick={() => onLoad(entry)}
    >
      <Stack gap={3}>
        {/* Row 1: hindcast name + action buttons */}
        <Group justify="space-between" wrap="nowrap" gap={4}>
          <Text size="xs" fw={600} lineClamp={1} style={{ flex: 1 }}>
            {entry.hindcast}
          </Text>
          <Group gap={2} wrap="nowrap">
            <Tooltip label="Re-run validation" withArrow position="left">
              <ActionIcon
                size="xs"
                variant="subtle"
                color="blue"
                onClick={(e) => { e.stopPropagation(); onRun(entry); }}
              >
                <IconPlayerPlayFilled size={10} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Remove" withArrow position="left">
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                onClick={(e) => { e.stopPropagation(); onRemove(entry.id); }}
              >
                <IconX size={12} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* Row 2: location + variable + radius */}
        <Text size="xs" c="dimmed">
          {entry.layer === "wave" ? "Waves" : "Wind"} · {formatCoord(entry.lon, entry.lat)} · {entry.radius} km
        </Text>

        {/* Row 3: key stats + time */}
        <Text size="xs" c="dimmed">
          Bias {biasStr} {unit} · RMSD {entry.stats.rmsd.toFixed(2)} {unit} · n={entry.stats.n.toLocaleString()} · {timeAgo(entry.timestamp)}
        </Text>
      </Stack>
    </Paper>
  );
}

export function RecentValidations() {
  const [open, setOpen] = useState(false);
  const { entries, removeEntry, clearAll } = useRecentStore();
  const { restoreValidation, openValidation } = useAppStore();

  if (entries.length === 0) return null;

  function handleLoad(entry: RecentValidation) {
    restoreValidation(entry.hindcast, entry.lon, entry.lat, entry.radius, entry.layer);
  }

  function handleRun(entry: RecentValidation) {
    restoreValidation(entry.hindcast, entry.lon, entry.lat, entry.radius, entry.layer);
    openValidation();
  }

  return (
    <Stack gap="xs">
      {/* Collapsible header */}
      <Group
        justify="space-between"
        align="center"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((o) => !o)}
      >
        <Group gap="xs" align="center">
          <Text size="sm" fw={500} c="#ace8f5">Recent validations</Text>
          <Badge size="xs" variant="light" color="gray" circle>
            {entries.length}
          </Badge>
        </Group>
        {open
          ? <IconChevronUp size={14} color="#ace8f5" />
          : <IconChevronDown size={14} color="#ace8f5" />
        }
      </Group>

      <Collapse in={open}>
        <Stack gap="xs">
          {entries.map((entry) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              onLoad={handleLoad}
              onRun={handleRun}
              onRemove={removeEntry}
            />
          ))}
          <Anchor
            size="xs"
            c="dimmed"
            onClick={(e) => { e.stopPropagation(); clearAll(); }}
            style={{ cursor: "pointer", alignSelf: "flex-end" }}
          >
            Clear all
          </Anchor>
        </Stack>
      </Collapse>
    </Stack>
  );
}
